/**
 * Wirkung mit aktuellem Stand: Wo stehen Wählergruppen, Verbände und Parteiflügel heute, wo nach dem Gesetz,
 * wie passt es zur Meinung im Land, und reicht der Haushalt? Genutzt im Gesetzentwurf. Reine Anzeige.
 */
import { useState, type ReactNode } from 'react';
import { usePref } from '../lib/pref';
import { useS } from '../store/game';
import { GROUPS, WEIGHT } from '../content/axes';
import { LOBBIES } from '../content/lobbies';
import { approval } from '../engine/govern';
import { debtLimit } from '../engine/budget';
import { lobbyDelta } from '../engine/lobbies';
import { de, sg } from '../engine/util';
import { Term } from './Term';
import { usePulse } from './Pulse';

/** Grenzen wie im Reiter „Stimmung“. */
export const MOOD_LO = 42, MOOD_HI = 55;
type Scale = { lo: number; hi: number; min: number; max: number; tl: string; tm: string; th: string };
const GROUP_SCALE: Scale = { lo: MOOD_LO, hi: MOOD_HI, min: 20, max: 80, tl: 'unzufrieden', tm: 'gemischt', th: 'zufrieden' };
const LOBBY_SCALE: Scale = { lo: 28, hi: 75, min: 0, max: 100, tl: 'Protest droht', tm: 'abwartend', th: 'lobt euch' };
const WING_SCALE: Scale = { lo: 25, hi: 60, min: 0, max: 100, tl: 'unzufrieden', tm: 'gemischt', th: 'zufrieden' };

/** Eine kompakte Zeile wie ein Werte-Balken im Spiel: Name, Leiste (heute gefüllt, Änderung farbig), Wert und Änderung. */
function MoodRow({ n, v, d, sc }: { n: ReactNode; v: number; d: number; sc: Scale }) {
  const P = (x: number) => Math.max(0, Math.min(100, (x - sc.min) / (sc.max - sc.min) * 100));
  const tone = (x: number) => (x >= sc.hi ? sc.th : x < sc.lo ? sc.tl : sc.tm), tc = (x: number) => (x >= sc.hi ? 'up' : x < sc.lo ? 'down' : 'warn');
  const nv = Math.max(0, Math.min(100, v + d)), dd = Math.abs(d) >= .5 ? nv - v : 0, lo = Math.min(v, nv);
  const flip = dd && tone(nv) !== tone(v), p = usePulse(Math.round(nv));
  return (
    <div className={'crow' + (p ? ' pulse' : '')} title={`${tone(v)}${flip ? ` → ${tone(nv)}` : ''}`}>
      <span className="n">{n}{flip ? <em className={tc(nv)}> → {tone(nv)}</em> : null}</span>
      <div className="cbar" aria-hidden="true">
        <i className={'cf ' + tc(lo)} style={{ width: `${P(lo)}%` }} />
        {dd !== 0 && <i className={dd > 0 ? 'cd up' : 'cd down'} style={{ left: `${P(lo)}%`, width: `${Math.abs(P(nv) - P(v))}%` }} />}
        <i className="tk" style={{ left: `${P(sc.lo)}%` }} /><i className="tk" style={{ left: `${P(sc.hi)}%` }} />
      </div>
      <span className="cv num"><b className={tc(nv)}>{de(nv, 0)}</b>{dd !== 0 && <span className={dd > 0 ? 'up' : 'down'}> {sg(dd, 0)}</span>}</span>
    </div>
  );
}
const Legend = ({ sc, extra }: { sc: Scale; extra?: ReactNode }) => <div className="gimpleg"><span>Striche: {sc.tl} unter {sc.lo}, {sc.th} ab {sc.hi}</span>{extra}</div>;

/** Aufklappbarer Abschnitt mit Zusammenfassung im Kopf. Ohne eigene Wahl offen, wenn sich etwas ändert. */
function Sect({ id, title, summary, auto, children }: { id: string; title: ReactNode; summary: ReactNode; auto: boolean; children: ReactNode }) {
  const [pref, setPref] = usePref<boolean | null>(`kabinett-wirkung-${id}`, null); const open = pref ?? auto;
  return (
    <div className={'gsec' + (open ? ' open' : '')}>
      <button type="button" className="gsech" aria-expanded={open} onClick={() => setPref(!open)}><span className="gimph">{title}</span><span className="gsum">{summary}</span></button>
      {open && <div className="gimp">{children}</div>}
    </div>
  );
}
type Row = { k: string; n: ReactNode; v: number; d: number };
/** Zeilen, die sich ändern, mit Leiste; unveränderte kompakt in einer Zeile (aufklappbar). */
function MoodList({ rows, sc, legend }: { rows: Row[]; sc: Scale; legend?: ReactNode }) {
  const [all, setAll] = useState(false);
  const ch = rows.filter((r) => Math.abs(r.d) >= .5), same = rows.filter((r) => Math.abs(r.d) < .5), show = all ? rows : ch;
  return <>
    {show.map((r) => <MoodRow key={r.k} n={r.n} v={r.v} d={r.d} sc={sc} />)}
    {!all && same.length > 0 && <button type="button" className="gsame" onClick={() => setAll(true)}>Unverändert: {same.map((r, i) => <span key={r.k}>{i ? ', ' : ''}{r.n} <b className="num">{de(r.v, 0)}</b></span>)}</button>}
    {(ch.length > 0 || all) && <Legend sc={sc} extra={legend} />}
  </>;
}
const upDown = (rows: Row[]) => { const u = rows.filter((r) => r.d >= .5).length, d = rows.filter((r) => r.d <= -.5).length; return u || d ? <>{u ? <span className="up">▲ {u}</span> : null}{u && d ? ' ' : ''}{d ? <span className="down">▼ {d}</span> : null}</> : <span className="sub">keine Änderung</span>; };

export function GroupImpact({ g }: { g: Record<string, number> }) {
  const S = useS(); const rows: Row[] = Object.keys(GROUPS).map((k) => ({ k, n: GROUPS[k], v: S.mood[k], d: g[k] || 0 }));
  return <Sect id="bev" title="Bevölkerung" summary={upDown(rows)} auto>
    <MoodList rows={rows} sc={GROUP_SCALE} />
  </Sect>;
}

/** Verbände (nur mit Einfluss „Verbände“) und Parteiflügel (nur eigene Partei, Einfluss „Flügel“). */
export function LobbyWingImpact({ fx, lobby, wings }: { fx: any; lobby: boolean; wings: boolean }) {
  const S: any = useS(); if (!lobby && !wings) return null;
  const d = lobby ? lobbyDelta(fx) : {}, p = fx.pos || {};
  const ds = Math.max(-10, Math.min(10, (p.sozial || 0) * 3 + (p.steuern || 0) * 1.5 + (p.wohnen || 0) + (p.klima || 0) * .5));
  const dw = Math.max(-10, Math.min(10, -(p.sozial || 0) * 1.5 + (p.buero || 0) * 2.5 - (p.steuern || 0) * 2 - Math.max(0, p.haushalt || 0)));
  const lr: Row[] = Object.keys(LOBBIES).map((k) => ({ k, n: LOBBIES[k].n, v: S.lob?.[k] ?? 50, d: d[k] || 0 }));
  const wr: Row[] = S.wing ? [{ k: 'soz', n: 'Sozialer Flügel', v: S.wing.soz, d: ds }, { k: 'wirt', n: 'Wirtschaftsflügel', v: S.wing.wirt, d: dw }] : [];
  return <>
    {lobby && <Sect id="lob" title="Verbände" summary={upDown(lr)} auto={lr.some((r) => Math.abs(r.d) >= .5)}><MoodList rows={lr} sc={LOBBY_SCALE} /></Sect>}
    {wings && wr.length > 0 && <Sect id="wing" title="Eure Partei" summary={upDown(wr)} auto={wr.some((r) => Math.abs(r.d) >= .5)}><MoodList rows={wr} sc={WING_SCALE} /></Sect>}
  </>;
}

/** Meinung im Land: Wo steht die Mehrheit, wo euer Gesetz? */
export function OpinionImpact({ parts, tot, w }: { parts: { a: string; n: string; law: number; pub: number; d: number }[]; tot: number; w: number }) {
  const X = (x: number) => `${((Math.max(-2, Math.min(2, x)) + 2) / 4 * 100).toFixed(1)}%`;
  const verdict = tot >= 1 ? 'kommt gut an' : tot >= .2 ? 'kommt eher gut an' : tot <= -1 ? 'stößt auf Ablehnung' : tot <= -.2 ? 'eher unbeliebt' : 'kaum ein Thema';
  return (
    <Sect id="zg" title="Meinung im Land" summary={<span className={tot >= .2 ? 'up' : tot <= -.2 ? 'down' : 'sub'}>{verdict}{Math.abs(tot) >= .2 ? ` (${sg(tot * w, 1)})` : ''}</span>} auto={parts.length > 0}>
      {parts.length ? <>
        {parts.slice(0, 4).map((x) => (
          <div key={x.a} className="crow" title={`Mehrheit ${x.pub >= .5 ? 'dafür' : x.pub <= -.5 ? 'dagegen' : 'unentschieden'}`}>
            <span className="n">{x.n}</span>
            <div className="cbar op" aria-hidden="true"><i className="mid" /><i className="pm" style={{ left: X(x.pub) }} /><i className={'pl ' + (x.d > 0 ? 'up' : 'down')} style={{ left: X(x.law) }} /></div>
            <span className="cv num"><b className={x.d > 0 ? 'up' : 'down'}>{x.d > 0 ? 'passt' : 'dagegen'}</b><span className={x.d > 0 ? 'up' : 'down'}> {sg(x.d * w, 1)}</span></span>
          </div>))}
        <div className="gimpleg"><span>| Mehrheit im Land · ◆ euer Gesetz · links dagegen, rechts dafür</span></div>
              </> : <span className="sub">Das Gesetz berührt keine Frage, über die das Land gerade streitet.</span>}
    </Sect>
  );
}

/** Zustimmung gesamt heute und nach voller Wirkung (Gruppen plus Meinung im Land). */
export function ApprovalTotal({ g, zg = 0 }: { g: Record<string, number>; zg?: number }) {
  const keys = Object.keys(GROUPS), a0 = approval(), a1 = a0 + keys.reduce((s, k) => s + (WEIGHT[k] || 0) * (Math.abs(g[k] || 0) >= .5 ? g[k] : 0), 0) + (Math.abs(zg) >= .1 ? zg : 0);
  return <div className="gimptot"><Term id="zustimmung">Zustimmung gesamt</Term><b className="num">{de(a0)} → <span className={a1 >= a0 ? 'up' : 'down'}>{de(a1)} %</span></b></div>;
}

export function BudgetImpact({ b, note }: { b: number; note?: string }) {
  const S = useS(); const a = S.saldo, n = a + (b || 0), lim = debtLimit();
  const m = (v: number) => Math.max(0, -v), M = Math.max(m(a), m(n), -lim, 1) * 1.15, w = (v: number) => `${(m(v) / M * 100).toFixed(1)}%`;
  const over = n < lim, was = a < lim;
  return (
    <div className="bimp">
      <div className="tr" aria-hidden="true">
        <i className="cur" style={{ width: w(Math.max(a, n)) }} />
        {n < a && <i className="add" style={{ left: w(a), width: `${((m(n) - m(a)) / M * 100).toFixed(1)}%` }} />}
        {n > a && <i className="save" style={{ left: w(n), width: `${((m(a) - m(n)) / M * 100).toFixed(1)}%` }} />}
        <i className="lim" style={{ left: w(lim) }} />
      </div>
      <div className="bimpr"><span>Saldo <b className="num">{sg(a)} → <span className={over ? 'down' : 'up'}>{sg(n)} Mrd</span></b> <b className={'num ' + ((b || 0) < 0 ? 'down' : 'up')}>({sg(b || 0)} Mrd im Jahr)</b></span><span><Term id="schuldenbremse">Schuldenbremse</Term> <b className="num limv">{de(lim, 0)} Mrd</b></span></div>
      <span className={'sub ' + (over ? 'down' : '')}>{over ? (was ? 'Schon jetzt über der Grenze: Das kostet laufend Zustimmung bei allen Gruppen.' : 'Damit rutscht ihr über die Schuldenbremse. Das kostet laufend Zustimmung.') : `Spielraum danach ${de(n - lim)} Mrd €.`}{note ? ` ${note}` : ''}</span>
    </div>
  );
}
